import {
  expandAustraliaAgeGroups,
  expandGradeLevel,
  parseAgeGroupValues,
} from "./helpers";

type LaunchedMultiCollection = {
  id?: string | null;
  grade?: string | null;
} | null;

type ResolveMcAnalyticsGradeInput = {
  /** The lesson's own grade range - ageGroup (AU) or gradeLevel (NA), a stringified array. */
  lessonGrade?: string | null;
  /** Every multi-collection the lesson belongs to. */
  multiCollections?: readonly LaunchedMultiCollection[] | null;
  /** The multi-collection the teacher actually launched from, if known. */
  mcId?: string | null;
};

/**
 * Both regions can express a span as a single combined label rather than as separate entries -
 * "Grade 3-4" and "Grade K-3" in North America, "Year 3-4" and "Kindy/Reception - Year 2" in
 * Australia. Counting those raw would read a two-year span as one grade, so expand through the
 * existing helpers first. Each expander passes the other region's labels through untouched, and
 * discrete values are unaffected.
 */
const expandGradeValues = (value?: string | null): string[] =>
  expandAustraliaAgeGroups(
    parseAgeGroupValues(value)
      .filter((entry): entry is string => typeof entry === "string")
      .flatMap((entry) => expandGradeLevel(entry)),
  );

/**
 * OP-3092: a lesson that spans several grades sits on one multi-collection card per grade, so the
 * lesson's own grade range cannot say which grade the teacher was actually teaching. When such a
 * lesson is launched from a single-grade card, report that card's grade instead.
 *
 * Returns undefined whenever the rule does not apply, so the caller falls back to the lesson's
 * range: single-grade lessons, unknown launch context, and cards that themselves span several
 * grades are all left exactly as they were.
 */
export const resolveMcAnalyticsGrade = ({
  lessonGrade,
  multiCollections,
  mcId,
}: ResolveMcAnalyticsGradeInput): string | undefined => {
  if (!mcId) return undefined;

  // The rule is scoped to multi-grade lessons only.
  if (expandGradeValues(lessonGrade).length < 2) return undefined;

  const launched = multiCollections?.find((mc) => mc?.id === mcId);
  const mcGrades = expandGradeValues(launched?.grade);

  // Only single-select cards identify a grade unambiguously.
  if (mcGrades.length !== 1) return undefined;

  // Match the shape `grade` already uses on these events: a stringified array.
  return JSON.stringify(mcGrades);
};
